import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { VStack } from '@astryxdesign/core/Stack';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Text, Heading } from '@astryxdesign/core/Text';
import { Icon } from '@astryxdesign/core/Icon';
import { Banner } from '@astryxdesign/core/Banner';
import { Bell, CalendarPlus } from '@phosphor-icons/react';
import { useTenant } from '@/app/tenant-context';
import { bookingIcs, downloadIcs } from '@/lib/ics';
import { pushSupport, subscribePush } from '@/lib/push';
import { dateTimeLabel } from '@/lib/format';
import type { PublicBooking } from '@/lib/types';

/** After booking: offer a push reminder 24 h before; otherwise a calendar file. */
export function ReminderOffer({ booking, token }: { booking: PublicBooking; token: string }) {
  const { api, slug, tenant } = useTenant();
  const [support] = useState(pushSupport);
  const [done, setDone] = useState(booking.reminder_opt_in);
  const sub = useMutation({
    mutationFn: async () => api.subscribePush(token, await subscribePush(slug)),
    onSuccess: () => setDone(true),
  });

  const addToCalendar = () =>
    downloadIcs(
      `zapis-${booking.code}.ics`,
      bookingIcs({
        id: booking.id,
        code: booking.code,
        serviceName: booking.service_name,
        startsAt: booking.starts_at,
        endsAt: booking.ends_at,
        studioName: tenant.name,
        address: tenant.address,
        phone: tenant.phone,
        url: `${window.location.origin}/s/${slug}/my/${booking.id}`,
      }),
    );

  if (booking.status !== 'confirmed') return null;

  return (
    <Card padding={4} variant="muted">
      <VStack gap={3}>
        <Heading level={3}>Напомнить за сутки?</Heading>
        {done ? (
          <Banner
            status="success"
            title="Напоминание включено"
            description={`Пришлём уведомление накануне визита (${dateTimeLabel(booking.starts_at, booking.timezone)}).`}
          />
        ) : support === 'supported' ? (
          <>
            <Text color="secondary">Пришлём уведомление на этот телефон. Отключить можно в «Моя запись».</Text>
            <Button label="Включить напоминание" variant="primary" icon={<Icon icon={Bell} />} isLoading={sub.isPending} onClick={() => sub.mutate()} />
            {sub.isError && <Text color="secondary">{(sub.error as Error).message}. Добавьте запись в календарь.</Text>}
          </>
        ) : (
          <Text color="secondary">
            {support === 'needs-install'
              ? 'На iPhone напоминания работают, когда сайт добавлен на экран «Домой» (Поделиться → На экран «Домой»). Пока можно добавить запись в календарь.'
              : support === 'denied'
                ? 'Уведомления запрещены в настройках браузера. Добавьте запись в календарь — он напомнит сам.'
                : 'Этот браузер не поддерживает уведомления. Добавьте запись в календарь — он напомнит сам.'}
          </Text>
        )}
        <Button label="Добавить в календарь" icon={<Icon icon={CalendarPlus} />} onClick={addToCalendar} />
      </VStack>
    </Card>
  );
}
